import { createTheme } from '@mui/material/styles';

/**
 * Palette categoriale per i grafici: il colore identifica il tipo di documento, non la sua
 * posizione in classifica. Valori presi dal mockup approvato di AUTO-213, pensati per fondo chiaro
 * e distinguibili anche nelle forme più deboli (protanopia/deuteranopia).
 *
 * Resta un export a sé e non entra in `palette` del tema: `primary.main` continua a governare i
 * controlli, questa governa solo le serie dei grafici. Oltre la sua lunghezza l'assegnazione
 * ricicla i colori (vedi utils/categoricalColors.js).
 */
export const categoricalPalette = [
  '#0052ee',
  '#c02f7a',
  '#d1571f',
  '#7a3fd4',
  '#00a094',
  '#0b7285',
];

// Tema personalizzato basato sullo stile dell'interfaccia mostrata
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: 'rgba(25, 118, 210, 0.1)',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
    divider: '#eeeeee',
    /**
     * Gli sfondi delle pastiglie di stato del tab Utenti (mockup screen-2): una tinta tenue
     * sotto un testo pieno, come le pastiglie blu dei ruoli qui sopra.
     *
     * Sono due token dedicati e non `success.light` per una ragione precisa: `success` non è
     * definito in questo tema, quindi arriva dai default di MUI, dove `light` è `#4caf50` —
     * un verde pieno, che a sfondo dà una pastiglia satura invece della tinta del mockup.
     * Ridefinirlo qui lo aggiusterebbe, ma cambierebbe anche i sei `Alert severity="success"`
     * dell'applicazione, che da quel token calcolano il proprio sfondo. Due token nuovi non
     * hanno raggio d'azione.
     *
     * I colori del *testo* invece esistono già e vanno bene: `success.main` dei default MUI è
     * `#2e7d32`, esattamente il verde del mockup, e `text.secondary` il grigio.
     */
    stato: {
      attivoSfondo: 'rgba(46, 125, 50, 0.1)',
      inattivoSfondo: '#eceff1',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 700,
      fontSize: '1.75rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          border: '1px solid #eeeeee',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        outlined: {
          border: '1px solid #1976d2',
          color: '#1976d2',
        },
        outlinedError: {
          border: '1px solid rgba(211, 47, 47, 0.5)',
          color: '#d32f2f',
        },    
        outlinedWarning: {
          border: '1px solid rgba(255, 94, 0, 0.81)',
          color: '#ff5e00',
        },    
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 4,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #eeeeee',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #eeeeee',
          padding: 16,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
        },
      },
    },
  },
});

export default theme; 